import { useDeferredValue, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { api, till } from "@/lib/api";
import { initials, personName, time } from "@/lib/format";
import { Badge, Input, Pager, Select } from "@/components/ui";
import { Card, PageHead, td, th } from "@/components/Layout";

type Entry = { id: string; area: string; action: string; target: string | null; details: string[]; actor: string; at: number };
const AREAS = ["Products", "Stock", "Staff", "Customers", "Cash", "Discounts", "Setup", "Security"];
const PAGE = 15;
const dayOf = (t: number) => new Date(t).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });

/** Who changed what in the back office, and what it was before. */
export function Activity() {
  const [area, setArea] = useState("");
  const [find, setFind] = useState("");
  const [page, setPage] = useState(1);
  const term = useDeferredValue(find.trim());
  const q = useQuery({
    queryKey: ["audit", area, term, page],
    queryFn: () => api<{ entries: Entry[]; total: number }>(till(`/admin/audit?area=${encodeURIComponent(area)}&q=${encodeURIComponent(term)}&limit=${PAGE}&offset=${(page - 1) * PAGE}`)),
    placeholderData: keepPreviousData,
  });
  const rows = q.data?.entries ?? [];
  const days = [...new Set(rows.map((e) => dayOf(e.at)))];
  return (
    <>
      <PageHead title="Activity log" sub="Who changed what in the back office. It can't be edited or deleted here." />
      <Card
        title="Changes"
        right={
          <div className="flex flex-[1_1_340px] flex-wrap justify-end gap-2">
            <Input aria-label="Search the activity log" value={find} onChange={(e) => { setFind(e.target.value); setPage(1); }} placeholder="Search a name or product" className="!h-10 min-w-0 flex-[1_1_180px] sm:max-w-[280px]" />
            <Select aria-label="Show" value={area} onChange={(e) => { setArea(e.target.value); setPage(1); }} className="!h-10 !w-auto">
              <option value="">Everything</option>
              {AREAS.map((a) => <option key={a} value={a}>{a}</option>)}
            </Select>
          </div>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead><tr>{["What changed", "Who", "Area"].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
            {days.map((day) => (
              <tbody key={day}>
                <tr><td colSpan={3} className="bg-slate-50 px-3.5 py-2 text-xs font-bold text-slate-600">{day}</td></tr>
                {rows.filter((e) => dayOf(e.at) === day).map((e) => (
                  <tr key={e.id}>
                    <td className={`${td} whitespace-normal`}>
                      <b className="block font-semibold">{e.action}{e.target ? `: ${e.target}` : ""}</b>
                      {e.details.length ? <span className="block text-[13px] text-slate-600">{e.details.join(" · ")}</span> : null}
                      <span className="text-xs text-slate-500">{time(e.at)}</span>
                    </td>
                    <td className={td}><span className="flex items-center gap-2"><span className="grid h-8 w-8 flex-none place-items-center rounded-full bg-brand-tint text-[11px] font-extrabold text-brand-ink">{initials(personName(e.actor))}</span>{personName(e.actor)}</span></td>
                    <td className={td}><Badge>{e.area}</Badge></td>
                  </tr>
                ))}
              </tbody>
            ))}
          </table>
        </div>
        {q.isError ? <p className="px-5 py-8 text-center text-sm text-slate-500">{q.error instanceof Error ? q.error.message : "Couldn't load the log"}</p> : q.data && !rows.length ? <p className="px-5 py-8 text-center text-sm text-slate-500">{find || area ? "Nothing matches." : "No changes recorded yet. They'll appear here from now on."}</p> : null}
        <Pager page={page} size={PAGE} total={q.data?.total ?? 0} onPage={setPage} />
      </Card>
    </>
  );
}
